import {Mapper} from '@s-ui/domain'

export class ProductStructuredDataSeoMapper extends Mapper {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Object} deps.config
   * @param {Function} deps.structuredDataValueObjectFactory
   * */
  constructor({config, structuredDataValueObjectFactory}) {
    super()
    this._config = config
    this._structuredDataValueObjectFactory = structuredDataValueObjectFactory
    this._vehicleRatingTypes = config.get('ratings').VEHICLE_RATING_CATEGORY
  }

  _getItemListSchema({list}) {
    return {
      '@type': 'ItemList',
      itemListElement: list.map((item, index) => {
        return {
          '@type': 'ListItem',
          position: index + 1,
          name: item
        }
      })
    }
  }

  _getReviewSchema({description, make, model, rating, pros, cons}) {
    const hasRatings = Boolean(rating?.length)
    const hasPros = Boolean(pros.length)
    const hasCons = Boolean(cons.length)
    const isIncludeReviewSchema = hasRatings && (hasPros || hasCons)
    if (!isIncludeReviewSchema) return {}
    const overallRating = rating.find(rating => rating.type === 'overall')
    const hasOverallRating = Boolean(overallRating)

    return {
      review: {
        '@type': 'Review',
        name: `${make} ${model} review`,
        reviewBody: description,
        author: {
          '@type': 'Organization',
          name: 'Coches.net'
        },
        ...(hasOverallRating && {
          '@type': 'Rating',
          ratingValue: overallRating.score,
          bestRating: '10'
        }),
        ...(hasPros && {positiveNotes: this._getItemListSchema({list: pros})}),
        ...(hasCons && {negativeNotes: this._getItemListSchema({list: cons})})
      }
    }
  }

  _getOfferSchema({lowPrice}) {
    const hasOffer = Boolean(lowPrice)
    if (!hasOffer) return {}
    return {
      offers: {
        '@type': 'AggregateOffer',
        lowPrice,
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock'
      }
    }
  }

  _getAdditionalItemsSchema({rating}) {
    const hasRatings = Boolean(rating?.length)
    if (!hasRatings) return {}
    const vehicleRatingTypesArray = Object.values(this._vehicleRatingTypes)

    return {
      additionalProperty: rating
        .filter(
          ratingItem =>
            ratingItem.type !== this._vehicleRatingTypes.OVERALL && vehicleRatingTypesArray.includes(ratingItem.type)
        )
        .map(ratingItem => {
          return {
            '@type': 'PropertyValue',
            name: ratingItem.label,
            value: ratingItem.score
          }
        })
    }
  }

  /**
   * https://schema.org/Product
   * @return {StructuredDataValueObject}
   */
  map = ({description, image, logo, lowPrice, make, model, name = '', rating, pros, cons}) => {
    return this._structuredDataValueObjectFactory({
      '@context': 'http://schema.org',
      '@type': 'Product',
      name,
      brand: {'@type': 'Brand', name: make},
      logo,
      image,
      description,
      model,
      itemCondition: 'http://schema.org/NewCondition',
      ...this._getOfferSchema({lowPrice}),
      ...this._getReviewSchema({description, make, model, rating, pros, cons}),
      ...this._getAdditionalItemsSchema({rating})
    })
  }
}
